/**
 * The same-origin `/play` proxy URL for launching a creation. THE one place the
 * play-URL shape is built (GamePage's Start + install, the Arena host/join).
 * The proxy serves the creation's bundle standalone + installable; the shell
 * reads only `?match=` (Arena pairing) and `?server=` (dev override) off the
 * query.
 *
 * Two shapes, and choosing between them is a real decision:
 *
 *  - {@link playUrl} FLOATS. It names the game, not a build, and the proxy
 *    resolves it to whatever version is live at that moment. This is the
 *    default — what Start opens, what a player installs, what a shared link
 *    should say — because anything a player keeps (a home-screen app, a
 *    bookmark, a link in a chat) outlives the version that was current when
 *    they saved it.
 *  - {@link pinnedPlayUrl} names an exact build and never moves. Use it only
 *    when a moving target would be WRONG: an online match must put both
 *    players on one identical build, and studio playtests, audits and
 *    version-vs-version comparison are all questions about a specific version.
 */

/** Play URL for a creation — always the live version (offline launch → menu). */
export function playUrl(creationId: string): string {
  return `/play/${creationId}/`;
}

/** Play URL for one exact version. Only where a moving version would be wrong. */
export function pinnedPlayUrl(creationId: string, version: number): string {
  return `/play/${creationId}/${version}/`;
}

/** Only a genuinely isolated HTTPS origin may be embedded inside Studio.
 * Generated `/play` bundles are deliberately same-origin for standalone game
 * launches and therefore must never become scripted iframes in the signed-in
 * portal. */
export function studioEmbeddedPreviewUrl(candidate: string, portalHref: string): string | null {
  const portal = new URL(portalHref);
  const preview = new URL(candidate, portal);
  if (preview.protocol !== 'https:' || preview.origin === portal.origin) return null;
  return preview.href;
}

/**
 * Online-match play URL. With a `token` the two invited players are paired into
 * one room and the shell auto-launches straight into the match (Arena invite);
 * without one it is the public quick-match entry. `online=1` marks the intent.
 *
 * PINNED on purpose: a match is two clients stepping one deterministic sim, so
 * both seats must load the same build. A floating URL could hand the guest a
 * version the host is not running.
 */
export function onlineMatchUrl(creationId: string, version: number, token?: string): string {
  const params = new URLSearchParams({ online: '1' });
  if (token) params.set('match', token);
  return `${pinnedPlayUrl(creationId, version)}?${params.toString()}`;
}
